<script lang="ts" setup>
  import { ref } from 'vue'
  import {
    KsBreadcrumbs,
    KsButton,
    KsCallout,
    KsCarousel,
    KsCarouselItem,
    KsDialog,
    KsDropdown,
    KsExpand,
    KsExpandItem,
    KsInput,
    KsPopover,
    KsProgress,
    KsSpinner,
    KsSwitch,
    KsTab,
    KsTabs,
    KsTextArea
  } from '@aschehoug/kloss'
  import AppLayout from '@/components/layout/AppLayout.vue'
  import HighlightedBox from '@/components/global/HighlightedBox.vue'
  import VgsButton from '@/components/common/VgsButton.vue'
  import Typography from '@/components/common/Typography.vue'
  import { useKlossToast } from '@/composables/useKlossToast'
  import StandardLayout from '@/components/layout/Layout.vue'
  import FlytTask from '@/components/task/FlytTask.vue'
  import ErrorCapturer from '@/components/common/ErrorCapturer.vue'
  import ColorBox from '@/components/global/ColorBox.vue'

  const dialogOpen = ref(false)
  const dropdownValue = ref('Apple')
  const dropdownOptions = ['Apple', 'Pear', 'Banana']
  const radioButton = ref('One')
  const password = ref('Secret')
  const breadcrumbs = [
    { title: 'Home', href: '#' },
    { title: 'Books', href: '#' },
    { title: 'Crime and Punishment', href: '#' },
    { title: 'About', href: '#' }
  ]
  const fileName = ref('Notes.docx')
  const klossToast = useKlossToast()

  function onToast() {
    klossToast.info('You have 5 unread messages')
  }

</script>

<template>
  <StandardLayout>
    <AppLayout>
      <div class="design-page">
        <Typography variant="h1">
          Design
        </Typography>
        <br>
        <div>
          <Typography variant="h2">
            VGS Features
          </Typography>
        </div>
        <br>

        <div>
          <ColorBox
            icon="Husk"
            title="Husk"
            text="Dett er noe viktig du må huske på."
            color-pair="Blue"
          />
        </div>

        <br>
        <div>
          <h2>Flyt</h2>
          <ErrorCapturer>
            <FlytTask id="a0ce152b-48cd-4c6a-8c7a-f2dc1f00d108" />
          </ErrorCapturer>
        </div>
        <br>
        <Typography variant="h2">
          Inputs
        </Typography>
        <div class="design-texts">
          <label class="design-text">
            Text:
            <KsInput
              type="text"
              placeholder="Text..."
            />
          </label>
          <label class="design-search">
            Search:
            <KsInput
              type="search"
              placeholder="Search..."
            />
          </label>
          <label class="design-dropdown">
            Select some tasty fruit:
            <KsDropdown
              v-model="dropdownValue"
              :options="dropdownOptions"
              :option-label="(opt: any) => opt"
              placeholder="Dropdown"
            />
          </label>
          <label class="design-textarea">
            Textarea:
            <KsTextArea
              placeholder="Write something..."
              rows="3"
            />
          </label>
          <label class="design-password">
            Password:
            <KsInput
              v-model="password"
              type="password"
              placeholder="Password..."
            />
          </label>
          <div class="design-inputs">
            <KsInput
              type="checkbox"
              checked
            />
            <KsInput type="checkbox" />
            <KsInput
              type="radio"
              name="radio"
            />
            <KsInput
              type="radio"
              name="radio"
              checked
            />
            <KsSwitch checked />
            <KsButton
              variant="secondary"
              type="label"
            >
              <KsInput type="checkbox" />
              Subscribe
            </KsButton>
            <KsButton
              variant="secondary"
              type="label"
            >
              <KsInput
                v-model="radioButton"
                type="radio"
                name="radio-button"
                value="One"
                checked
              />
              One
            </KsButton>
            <KsButton
              variant="secondary"
              type="label"
            >
              <KsInput
                v-model="radioButton"
                type="radio"
                name="radio-button"
                value="Two"
              />
              Two
            </KsButton>
            <KsInput
              type="range"
              style="width: 150px"
            />
            <KsProgress :values="[20]" />
          </div>
        </div>
        <Typography variant="h2">
          Buttons
        </Typography>
        <div class="design-buttons">
          <KsButton variant="primary">
            Primary
          </KsButton>
          <KsButton variant="secondary">
            Secondary
          </KsButton>
          <KsButton variant="tertiary">
            Tertiary
          </KsButton>
          <KsButton
            variant="primary"
            icon-left="heart"
          >
            Like
          </KsButton>
          <KsButton
            variant="secondary"
            shape="square"
            icon-left="house"
          />
          <KsButton
            v-ks-tooltip.down="'Tooltip'"
            variant="secondary"
          >
            Hover me
          </KsButton>
          <VgsButton
            variant="flat-sharp"
            icon-left="life-ring"
          >
            Hint
          </VgsButton>
        </div>
        <Typography variant="h2">
          Popover and dialog
        </Typography>
        <div class="design-popover-dialog">
          <KsPopover
            title="Popover"
            direction="down left"
          >
            <template #toggle>
              <KsButton
                variant="primary"
                icon-right="ellipsis"
              >
                Open popover
              </KsButton>
            </template>
            <template #content>
              <KsButton stretch>
                Open
              </KsButton>
              <KsButton
                icon-left="share"
                href="#"
                stretch
                @click.prevent="() => {}"
              >
                Share
              </KsButton>
              <KsButton
                icon-left="trash"
                stretch
              >
                Delete
              </KsButton>
              <KsButton
                stretch
                disabled
              >
                Disabled
              </KsButton>
            </template>
          </KsPopover>
          <KsButton
            variant="primary"
            icon-right="floppy-disk"
            @click="dialogOpen = !dialogOpen"
          >
            Save dialog
          </KsButton>
          <Teleport to="#app">
            <KsDialog
              title="Save"
              :open="dialogOpen"
              @close="dialogOpen = false"
            >
              <template #body>
                <div>
                  <span>You have unsaved changes. Do you want to save file?</span>
                  <label>
                    File name
                    <KsInput
                      v-model="fileName"
                      type="text"
                      placeholder="..."
                    />
                  </label>
                </div>
              </template>
              <template #footer>
                <div class="design-dialog-buttons">
                  <KsButton
                    variant="tertiary"
                    @click="dialogOpen = !dialogOpen"
                  >
                    Cancel
                  </KsButton>
                  <KsButton
                    variant="primary"
                    icon-left="floppy-disk"
                    @click="dialogOpen = !dialogOpen"
                  >
                    Save
                  </KsButton>
                </div>
              </template>
            </KsDialog>
          </Teleport>
        </div>
        <Typography variant="h2">
          Breadcrumbs
        </Typography>
        <div class="design-breadcrumbs">
          <KsBreadcrumbs :items="breadcrumbs" />
        </div>
        <Typography variant="h2">
          Callouts
        </Typography>
        <div class="design-callout">
          <KsCallout variant="info">
            You have 3 new notifications
          </KsCallout>
          <KsCallout
            variant="warning"
            title="3 days left"
          >
            Remember to renew license
          </KsCallout>
          <KsCallout
            variant="error"
            title="Oops!"
          >
            Something went wrong
          </KsCallout>
        </div>
        <Typography variant="h2">
          Highlighted box
        </Typography>
        <div class="design-flex-column">
          <HighlightedBox
            width="full"
            label="Formel"
            text="f(x) = ax + bx + cx + d, der a = 0"
            text-alignment="center"
          />
          <HighlightedBox
            label="Oppgave 1"
            text="a) f(x) = ax + bx + cx + dx + ex + f, der a = 0 <br> b) f(x) = ax + bx + cx + d, der a = 0"
            text-alignment="left"
          />
        </div>
        <Typography variant="h2">
          Expand
        </Typography>
        <div class="design-expand">
          <KsExpand size="medium">
            <KsExpandItem title=" 1. Intro">
              <template #content>
                This is the intro
              </template>
            </KsExpandItem>
            <KsExpandItem title="2. Main part">
              <template #content>
                This is the main part
              </template>
            </KsExpandItem>
            <KsExpandItem title=" 3. Ending">
              <template #content>
                This is the ending
              </template>
            </KsExpandItem>
          </KsExpand>
          <div class="design-expand-custom">
            <KsExpand size="medium">
              <KsExpandItem
                title="Solution"
                shape="rounded"
              >
                <template #toggle>
                  1. Solve this task
                </template>
                <template #content>
                  <p>This is the first solution</p>
                </template>
              </KsExpandItem>
              <KsExpandItem
                title="Solution"
                shape="rounded"
              >
                <template #toggle>
                  2. Solve that task
                </template>
                <template #content>
                  <p>This is the second solution</p>
                </template>
              </KsExpandItem>
              <KsExpandItem
                title="Solution"
                shape="rounded"
              >
                <template #toggle>
                  3. Solve the task
                </template>
                <template #content>
                  <p>This is the third solution</p>
                </template>
              </KsExpandItem>
            </KsExpand>
          </div>
          <div class="design-expand-custom">
            <KsExpand size="medium">
              <KsExpandItem title="Solution">
                <template #toggle>
                  Solve this task
                </template>
                <template #content>
                  <p>This is the first solution</p>
                </template>
              </KsExpandItem>
              <KsExpandItem title="Solution">
                <template #toggle>
                  Solve that task
                </template>
                <template #content>
                  <p>This is the second solution</p>
                </template>
              </KsExpandItem>
              <KsExpandItem title="Solution">
                <template #toggle>
                  Solve the task
                </template>
                <template #content>
                  <p>This is the third solution</p>
                </template>
              </KsExpandItem>
            </KsExpand>
          </div>
        </div>
        <Typography variant="h2">
          Tabs
        </Typography>
        <KsTabs>
          <KsTab title="Tab 1">
            This is the tabpanel for tab 1
          </KsTab>
          <KsTab
            title="Tab 2 disabled"
            disabled
          />
          <KsTab title="Tab 3">
            This is the tabpanel for tab 3
          </KsTab>
          <KsTab title="Tab 4">
            This is the tabpanel for tab 4
          </KsTab>
        </KsTabs>
        <Typography variant="h2">
          Carousel
        </Typography>
        <div class="design-carousel">
          <KsCarousel>
            <KsCarouselItem
              v-for="item of 20"
              :key="item"
            >
              <div class="design-carousel-item">
                Box {{ item }}
              </div>
            </KsCarouselItem>
          </KsCarousel>
        </div>
        <Typography variant="h2">
          Spinner
        </Typography>
        <KsSpinner size="small" />
        <Typography variant="h2">
          Toast
        </Typography>
        <KsButton
          variant="secondary"
          icon-right="bell"
          @click="onToast"
        >
          Trigger toast
        </KsButton>
      </div>
      <Typography variant="h2">
        Image with credits
      </Typography>
    </AppLayout>
  </StandardLayout>
</template>

<style scoped>

  .design-bylines {
    display: flex;
    flex-flow: row wrap;
    gap: 2rem;
  }

  .design-flex-column {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .design-page {
    padding-bottom: 2rem;
    max-width: 1200px;
    width: 95%;
    margin: 0 auto;
  }

  .design-texts {
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }

  .design-text,
  .design-search,
  .design-dropdown {
    grid-column: span 4;
  }

  .design-textarea,
  .design-password {
    grid-column: span 6;
  }

  .design-buttons,
  .design-inputs {
    grid-column: span 12;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .8rem;
  }

  .design-popover-dialog {
    display: flex;
    gap: 1rem;
  }

  .design-dialog-buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
  }

  .design-breadcrumbs {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 1rem 0;
  }

  .design-callout {
    display: flex;
    align-items: start;
    gap: 1rem;
    margin: 1rem 0;
  }

  .design-expand {
    display: flex;
    gap: 1rem;
  }

  .design-expand-custom {
    --ks-input: white;

    padding: 1em;
    background: var(--color-lightblue-20);
    border-radius: 12px;
  }

  .design-expand-custom,
  .design-breadcrumbs > *,
  .design-expand > * {
    flex-basis: 50%;
  }

  .design-carousel {
    margin: 0 1rem;
    max-width: 750px;
  }

  .design-carousel-item {
    width: 150px;
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgb(0 0 0 / 20%);
    border-radius: 12px;
  }
</style>
